import { useState, useEffect, useContext, useRef } from "react";
import Box from "@mui/material/Box";
import { useMediaQuery, Button, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Drawer from "./Drawer";
import Header from "./Header";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import OverView from "./OverView";
import LoadingFallBack from "./LoadingFallBack";
import NotFound from "./NotFound";
import Chart from "./Chart";
import PlaceHolder from "./PlaceHolder";
import Form from "./Form";
import useCheckLogin from "hooks/useCheckLogin";
import useCheckTabActivityDetector from "hooks/useCheckActiveTab";
import {
  ResultCarPrice,
  onRoadPriceResult,
  CarPriceInflation,
  carEstimatePrice,
} from "./types";
import WheelAlignmentCost from "./WheelAlignmentCost";
import UserContext from "userContext";
import { clearLocalStorage } from "Common/common";
import RattingDialog from "./ratting";
import { ToastContainer } from "react-toastify";

export default function Result() {
  const [carPriceValue, setCarPriceValue] = useState<ResultCarPrice>({
    carCompany: "",
    carModel: "",
    carVersion: "",
    year: "",
    maxPrice: null,
    carValue: 0,
    minPrice: null,
    isWarning: false,
    listedPrice: null,
    minListedPrice: null,
    maxListedPrice: null,
    manufacturerListPrice: null,
    hanoiOnRoadPrice: null,
    generalOnRoadPrice: null,
    hcmOnRoadPrice: null,
    additions: [],
  });

  const [jobId, setJobId] = useState<string | null>(null);

  const [savedPrevCarPrice, setSavedPrevCarPrice] = useState<
    ResultCarPrice | undefined
  >();
  const prevCarPriceValueRef = useRef<ResultCarPrice | undefined>(undefined);

  const [onRoadPrice, setOnRoadPrice] = useState<onRoadPriceResult>({
    listedPrice: 0,
    registrationFee: 0,
    registrationFeePercent: 0,
    licensePlateFee: 0,
    vehicleInspectionFee: 0,
    roadMaintenanceFee: 0,
    liabilityInsurancePremium: 0,
    totalPrice: 0,
  });

  const [priceVolatility, setPriceVolatility] = useState<CarPriceInflation[]>(
    [],
  );

  const [carEstimatedPrice, setCarEstimatedPrice] = useState<carEstimatePrice>({
    avgPriceSlippage: 0,
    avgPriceAfterNextYear: 0,
    percentPriceReduceAfterNextYear: 0,
    priceReduceAfterNextYear: 0,
    avgPriceAfterNext2Year: 0,
    percentPriceReduceAfterNext2Year: 0,
    priceReduceAfterNext2Year: 0,
  });

  const [isLoadCarPrice, setIsLoadCarPrice] = useState(false);
  const [errorVolatility, setErrorVolatility] = useState<boolean>(false);
  const [errorCarEstimate, setErrorCarEstimate] = useState<boolean>(false);
  const [openPopupRating, setOpenPopupRating] = useState<boolean>(false);
  const [isClickOut, setIsClickOut] = useState<boolean>(false);
  const [isReset, setIsReset] = useState<boolean>(false);

  const [registerPlace, setRegisterPlace] = useState<string>("hanoi");

  const { user } = useContext(UserContext);

  const navigate = useNavigate();
  const theme = useTheme();

  const isTabletOrMobile = useMediaQuery("(max-width: 1050px)");
  const type = localStorage.getItem("type");

  useEffect(() => {
    setSavedPrevCarPrice(prevCarPriceValueRef.current);
    prevCarPriceValueRef.current = JSON.parse(JSON.stringify(carPriceValue));
  }, [carPriceValue]);

  const onBackToLandingPage = () => {
    setIsClickOut(true);
    if (carPriceValue.sessionId !== undefined && user?.enablePopup) {
      setOpenPopupRating(true);
    } else {
      navigate("/", { replace: true });
      clearLocalStorage();
    }
  };

  useEffect(() => {
    if (errorVolatility) {
      setPriceVolatility([]);
    }
  }, [errorVolatility]);

  useCheckLogin();
  useCheckTabActivityDetector();

  const handleReset = () => {
    if (!isReset) {
      setIsReset(true);
      setCarPriceValue({ ...carPriceValue, carValue: 0 });
    }
  };

  return (
    <>
      <ToastContainer />
      {isTabletOrMobile && (
        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? "#21262C" : "unset",
            paddingX: "20px",
          }}
        >
          <Header onBackToLandingPage={onBackToLandingPage} />
          <Divider />
          <Box sx={{ paddingY: "20px" }}>
            <Form
              carPriceValue={carPriceValue}
              setCarPriceValue={setCarPriceValue}
              setOnRoadPrice={setOnRoadPrice}
              setPriceVolatility={setPriceVolatility}
              setCarEstimatedPrice={setCarEstimatedPrice}
              isLoadCarPrice={isLoadCarPrice}
              setIsLoadCarPrice={setIsLoadCarPrice}
              setErrorVolatility={setErrorVolatility}
              setErrorEstimated={setErrorCarEstimate}
              registerPlace={registerPlace}
              jobId={jobId}
              setJobId={setJobId}
            />
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex" }}>
        <Drawer
          carPriceValue={carPriceValue}
          setPriceVolatility={setPriceVolatility}
          setCarPriceValue={setCarPriceValue}
          setOnRoadPrice={setOnRoadPrice}
          setCarEstimatedPrice={setCarEstimatedPrice}
          isLoadCarPrice={isLoadCarPrice}
          setIsLoadCarPrice={setIsLoadCarPrice}
          setErrorVolatility={setErrorVolatility}
          setErrorEstimated={setErrorCarEstimate}
          registerPlace={registerPlace}
          onBackToLandingPage={onBackToLandingPage}
          jobId={jobId}
          setJobId={setJobId}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.mode === "dark" ? "#272f38" : "#F7F8FE",
            minHeight: "100vh",
            width: "100%",
            px: "40px",
          }}
        >
          {!isTabletOrMobile && (
            <Header onBackToLandingPage={onBackToLandingPage} />
          )}
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mt: isTabletOrMobile ? 2 : 0,
              }}
            >
              <Button
                sx={{
                  textTransform: "unset",
                  fontSize: "20px",
                  border: "none",
                  color: theme.palette.mode === "dark" ? "#ffff" : "#21252C",
                  ":hover": {
                    border: "none",
                    backgroundColor: "transparent",
                  },
                  textAlign: "left",
                }}
                onClick={onBackToLandingPage}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
              >
                Giá trị xe
              </Button>
            </Box>
            {carPriceValue.carValue === null && !isLoadCarPrice && !jobId && (
              <NotFound handleReset={handleReset} />
            )}
            {!jobId &&
              carPriceValue.carValue !== null &&
              carPriceValue.carValue !== 0 && (
                <OverView
                  isLoading={isLoadCarPrice}
                  carvalue={carPriceValue.carValue}
                  applicationPriceRange={{
                    minPrice: carPriceValue.minPrice,
                    maxPrice: carPriceValue.maxPrice,
                  }}
                  listedPrice={carPriceValue.listedPrice}
                  listedPriceRange={{
                    minListedPrice: carPriceValue.minListedPrice,
                    maxListedPrice: carPriceValue.maxListedPrice,
                  }}
                  manufacturerListPrice={carPriceValue.manufacturerListPrice}
                  isAiGenerated={carPriceValue.isAiGenerated}
                />
              )}
          </>

          {carPriceValue.carValue === 0 && <PlaceHolder />}

          {jobId && <LoadingFallBack />}

          {type === "car" && carPriceValue.carValue !== 0 && (
            <WheelAlignmentCost
              registerPlace={registerPlace}
              setRegisterPlace={setRegisterPlace}
              onRoadPrice={onRoadPrice}
              setOnRoadPrice={setOnRoadPrice}
            />
          )}
          {type === "car" && carPriceValue.carValue !== 0 && (
            <Chart
              priceVolatility={priceVolatility}
              carEstimatePrice={carEstimatedPrice}
              error={errorVolatility}
              errorEstimated={errorCarEstimate}
            />
          )}
        </Box>
      </Box>
      {user?.enablePopup && (
        <RattingDialog
          open={openPopupRating}
          setOpen={setOpenPopupRating}
          sessionId={
            !isClickOut
              ? savedPrevCarPrice?.sessionId
              : carPriceValue?.sessionId
          }
          isClickOut={isClickOut}
          setIsClickOut={setIsClickOut}
        />
      )}
    </>
  );
}
